// Plain data: the drawing's objects as JSON would have them. ONE equality and
// ONE copy for everything that keeps an object to tell later whether it changed
// (changeTracker.ts, section.ts's recorded events, the memoised derivations):
// a key whose value is undefined is no key, NaN equals NaN, a Float64Array is
// compared by value; a `skip` set leaves those keys out at any depth. And ONE
// memo on top of them (`contentMemo`): a derivation is made again only when what
// it reads differs from what it was made from — compared, never serialised.
// DOM-free (test/plainData.test.mjs).

/** The fields of a drawing object that no derivation of its geometry reads: a
 *  product's DXF drawing and image, its names, ports, preparations and
 *  properties. Comparing them on every look would cost the most and say nothing. */
export const HEAVY_FIELDS: ReadonlySet<string> = new Set(['geometry', 'imageDataUrl', 'nameI18n', 'ports', 'preparations', 'properties']);

/** Are two keys the same data — as their JSON would be (a key whose value is
 *  undefined is none), without building it? */
export function sameData(a: unknown, b: unknown, skip?: ReadonlySet<string>): boolean {
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return typeof a === 'number' && typeof b === 'number' && Number.isNaN(a) && Number.isNaN(b);
  if (a instanceof Float64Array || b instanceof Float64Array) {
    if (!(a instanceof Float64Array) || !(b instanceof Float64Array) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i] && !(Number.isNaN(a[i]) && Number.isNaN(b[i]))) return false;
    return true;
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (!sameData(a[i], b[i], skip)) return false;
    return true;
  }
  const x = a as Record<string, unknown>, y = b as Record<string, unknown>;
  let n = 0;
  for (const k in x) { if (x[k] === undefined || skip?.has(k)) continue; if (!sameData(x[k], y[k], skip)) return false; n++; }
  for (const k in y) if (y[k] !== undefined && !skip?.has(k)) n--;
  return n === 0;
}

/** A copy of plain data, to keep as a key. */
export function copyData<V>(v: V, skip?: ReadonlySet<string>): V {
  if (typeof v !== 'object' || v === null) return v;
  if (v instanceof Float64Array) return v.slice() as V;
  if (Array.isArray(v)) return v.map((x) => copyData(x, skip)) as V;
  const out: Record<string, unknown> = {};
  for (const k in v) if (!skip?.has(k)) out[k] = copyData((v as Record<string, unknown>)[k], skip);
  return out as V;
}

/** Derivations kept by slot, each made again only when what it reads changed. */
export interface ContentMemo<V> {
  /** The value kept in `slot` if what it reads (`input()`) is the same data it
   *  was made from (`sameData`, the memo's skip set left out), else `make()`,
   *  kept with `input()` read again AFTER it: a derivation may fill in a default
   *  the first time (a room's `doors`), and the kept copy must hold it. */
  get(slot: string, input: () => unknown, make: () => V): V;
  /** Slots kept now (a test seam). */
  size(): number;
}

/** A memo of up to `max` slots, the least recently used out first. What it
 *  hands out is shared: never mutate it. */
export function contentMemo<V>(opts: { max?: number; skip?: ReadonlySet<string> } = {}): ContentMemo<V> {
  const max = opts.max ?? 256, skip = opts.skip;
  const kept = new Map<string, { input: unknown; value: V }>();
  return {
    get(slot, input, make) {
      const k = kept.get(slot);
      if (k && sameData(input(), k.input, skip)) {
        kept.delete(slot);
        kept.set(slot, k);
        return k.value;
      }
      const value = make();
      kept.delete(slot);
      kept.set(slot, { input: copyData(input(), skip), value });
      if (kept.size > max) kept.delete(kept.keys().next().value!);
      return value;
    },
    size: () => kept.size,
  };
}
